import { useState, useEffect, useCallback } from 'react';
import { useInput, useRecordContext, useGetList } from 'react-admin';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  IconButton,
  Button,
  Alert,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import type { FacetGroup } from '@src/facets';
import { JsonField } from './JsonField';

interface FamilyRow {
  tag: string;
  values: string[];
}

function recordToRows(family: Record<string, unknown>): FamilyRow[] {
  return Object.entries(family).map(([tag, values]) => ({
    tag,
    values: Array.isArray(values) ? values.filter((v): v is string => typeof v === 'string') : [],
  }));
}

function rowsToRecord(rows: FamilyRow[]): Record<string, string[]> {
  const record: Record<string, string[]> = {};
  for (const row of rows) {
    if (row.tag) {
      record[row.tag] = row.values;
    }
  }
  return record;
}

/**
 * Structured editor for the variant SKU family field.
 * Replaces the raw JSON textarea with facet-driven tag + values selectors.
 */
export const FamilyField = () => {
  const record = useRecordContext();
  const { field } = useInput({ source: 'family' });
  const { data: facetGroups = [], isLoading, error } = useGetList<FacetGroup>('facets');
  const [rows, setRows] = useState<FamilyRow[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && record?.family) {
      const initialRows = recordToRows(record.family as Record<string, unknown>);
      setRows(initialRows);
      setInitialized(true);
    }
  }, [record, initialized]);

  const updateRows = useCallback((newRows: FamilyRow[]) => {
    setRows(newRows);
    field.onChange(rowsToRecord(newRows));
  }, [field]);

  const handleAddRow = useCallback(() => {
    updateRows([...rows, { tag: '', values: [] }]);
  }, [rows, updateRows]);

  const handleRemoveRow = useCallback((index: number) => {
    updateRows(rows.filter((_, i) => i !== index));
  }, [rows, updateRows]);

  const handleTagChange = useCallback((index: number, newTag: string | null) => {
    const newRows = [...rows];
    newRows[index] = { tag: newTag ?? '', values: [] };
    updateRows(newRows);
  }, [rows, updateRows]);

  const handleValuesChange = useCallback((index: number, newValues: string[]) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], values: newValues };
    updateRows(newRows);
  }, [rows, updateRows]);

  const getAvailableTagChoices = useCallback((currentRowIndex: number): string[] => {
    const usedTags = new Set(
      rows.filter((_, i) => i !== currentRowIndex).map(r => r.tag).filter(Boolean)
    );
    return facetGroups.map(fg => fg.tag).filter(tag => !usedTags.has(tag));
  }, [rows, facetGroups]);

  const getValueChoices = useCallback((tag: string): string[] => {
    const facetGroup = facetGroups.find(fg => fg.tag === tag);
    return facetGroup?.values.map(v => v.name) ?? [];
  }, [facetGroups]);

  // Fall back to raw JSON editor if facets can't be loaded
  if (error) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="warning" sx={{ mb: 1 }}>
          Could not load facets. Falling back to JSON editor.
        </Alert>
        <JsonField source="family" label="Family (JSON)" />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Family
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Map facet tags to their values. Each tag can appear only once.
      </Typography>

      {isLoading ? (
        <Typography variant="body2" color="text.secondary">
          Loading facets...
        </Typography>
      ) : (
        <>
          {rows.map((row, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1.5 }}
            >
              <Autocomplete
                sx={{ flex: 1 }}
                size="small"
                options={getAvailableTagChoices(index)}
                value={row.tag || null}
                onChange={(_, newValue) => handleTagChange(index, newValue)}
                renderInput={params => (
                  <TextField {...params} label="Facet Tag" placeholder="Select a tag" />
                )}
              />

              <Autocomplete
                sx={{ flex: 2 }}
                size="small"
                multiple
                options={getValueChoices(row.tag)}
                value={row.values}
                onChange={(_, newValues) => handleValuesChange(index, newValues)}
                disabled={!row.tag}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Values"
                    placeholder={row.tag ? 'Select values' : 'Select a tag first'}
                  />
                )}
              />

              <IconButton
                size="small"
                onClick={() => handleRemoveRow(index)}
                color="error"
                title="Remove this facet"
                sx={{ mt: 0.5 }}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Box>
          ))}

          <Button
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddRow}
            sx={{ mt: 1 }}
          >
            Add Facet
          </Button>
        </>
      )}
    </Box>
  );
};
